import { useInfiniteQuery, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate, useRouteContext } from '@tanstack/react-router';
import { Button, ConfigProvider, Flex, Grid, Modal, Segmented, message } from 'antd';
import { isEqual } from 'lodash';
import { ChevronDown, CircleDot, KanbanIcon, ListChecks, Table } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState, useTransition } from 'react';
import { z } from 'zod';

import {
  countRecords,
  getCountRecordsUrl,
  getListSmartViewAvailableFieldsQueryKey,
  getLoadRecordDetailsUrl,
  getSearchRecordsUrl,
  loadRecordDetails,
  searchRecords,
  useBulkArchiveContacts,
  useBulkArchiveLeads,
  useListCustomFields,
  useListSmartViewAvailableFields,
  type BulkRecipientsBodyDto,
  type LoadRecordDetailsResponseDtoItemsItem,
  type SearchRecordsBodyDto,
} from '../../../api';
import { useCommunicationCapabilities, usePermissions } from '../../../modules/account/hooks';
import { useAiFlashRows, useAiUiSnapshot } from '../../../modules/ai-next/hooks';
import { useAiAssistant } from '../../../modules/ai-next/providers';
import { populateCommsRow } from '../../../modules/comms-rollup/utils';
import { SmsForm } from '../../../modules/communications/components/sms-form';
import { EmailForm } from '../../../modules/connection/components/email-form';
import { CreateCustomFieldDrawer } from '../../../modules/custom-field/components/custom-field-drawer';
import { customFieldRecordTypeLabels } from '../../../modules/custom-field/constants';
import { normalizeCustomFieldCreateType } from '../../../modules/custom-field/utils/normalize-create-type';
import { buildDeals, type DealDisplay } from '../../../modules/deal/utils/build-deals';
import { GlobalSearch } from '../../../modules/global-search/components';
import { HeaderActions } from '../../../modules/navigation/components/header-actions';
import { SelectionActionsBar } from '../../../modules/navigation/components/selection-actions-bar';
import {
  useAssignOwnerAction,
  useCallRecordAction,
  useMergeRecordsAction,
  useRunAgentsAction,
} from '../../../modules/navigation/hooks';
import { CreateRecordDrawer } from '../../../modules/records/components/create-record-drawer';
import {
  CustomFieldKanban,
  RecordKanbanEmptyPreview,
} from '../../../modules/records/components/custom-field-kanban';
import { CrmEmptyStateLayout } from '../../../modules/records/components/empty-states';
import { RecordGridCell } from '../../../modules/records/components/grid-cells';
import { RECORD_PREVIEW_RECORDS } from '../../../modules/records/constants';
import {
  buildColumnsFromFields,
  buildCommsPickerGroups,
  normalizeColumnPickerFieldType,
  SmartViewNoResultsEmpty,
  SmartViewTable,
  SmartViewTabs,
  tableColumnsToSmartViewColumns,
  useSmartView,
  type SmartViewColumnConfig,
} from '../../../modules/smart-view';
import { ColumnPickerButton } from '../../../ui/components/column-picker-button';
import { useTableSelection, type DataGridColumn } from '../../../ui/components/data-grid';
import { TableHeaderLabel } from '../../../ui/components/data-grid/header';
import { FloatingWindow } from '../../../ui/components/floating-window';
import { GroupByButton } from '../../../ui/components/group-by-button';
import { SortByButton } from '../../../ui/components/sort-by-button';
import { TableSurface } from '../../../ui/components/table-surface';
import { TextEllipsis } from '../../../ui/components/text';
import { useDebouncedValue, useDensity, useNavigationPosition } from '../../../ui/hooks';
import { useLayoutInsets } from '../../../ui/providers/layout-insets';
import { formatDateTime } from '../../../ui/utils/format-date';

import './records.css';

type RecordRow = {
  id: string;
  leadId: string | null;
  contactId: string | null;
  otherPeopleCount: number;
  contactFirstName: string;
  contactLastName: string;
  contactEmails: NonNullable<LoadRecordDetailsResponseDtoItemsItem['contact']>['emails'];
  contactPhoneNumbers: NonNullable<
    LoadRecordDetailsResponseDtoItemsItem['contact']
  >['phoneNumbers'];
  leadWebsiteId: string | null;
  leadWebsiteValue: string;
  customFields: Record<
    string,
    NonNullable<LoadRecordDetailsResponseDtoItemsItem['lead']>['customFields'][number]
  >;
  name: string;
  contact: string;
  email: string;
  phone: string;
  owners: {
    organizationMembershipId: string;
    userId: string;
    avatarIndex?: number | null;
    firstName: string;
    lastName: string;
  }[];
  deals: DealDisplay[];
  contactCreated: string;
  contactUpdated: string;
  created: string;
  updated: string;
  __previewKind?: 'mock' | 'skeleton' | 'loading' | 'pending';
  previewOwnerLabel?: string;
  previewDealsLine?: string;
  [key: string]: unknown;
};

const buildBlankRow = (id: string): RecordRow => ({
  id,
  leadId: null,
  contactId: null,
  otherPeopleCount: 0,
  contactFirstName: '',
  contactLastName: '',
  contactEmails: [],
  contactPhoneNumbers: [],
  leadWebsiteId: null,
  leadWebsiteValue: '',
  customFields: {},
  name: '',
  contact: '',
  email: '',
  phone: '',
  owners: [],
  deals: [],
  contactCreated: '',
  contactUpdated: '',
  created: '',
  updated: '',
});

const rowByDetails = new WeakMap<LoadRecordDetailsResponseDtoItemsItem, RecordRow>();

const buildRecordRow = (details: LoadRecordDetailsResponseDtoItemsItem): RecordRow => {
  const cached = rowByDetails.get(details);
  if (cached) {
    return cached;
  }

  const { lead, contact } = details;
  const row: RecordRow = {
    ...buildBlankRow(details.id),
    leadId: lead?.id ?? null,
    contactId: contact?.id ?? null,
    otherPeopleCount: Math.max(details.contactCount - 1, 0),
    contactFirstName: contact?.firstName ?? '',
    contactLastName: contact?.lastName ?? '',
    contactEmails: contact?.emails ?? [],
    contactPhoneNumbers: contact?.phoneNumbers ?? [],
    leadWebsiteId: lead?.websites[0]?.id ?? null,
    leadWebsiteValue: lead?.websites[0]?.value ?? '',
    name: lead?.name ?? '',
    contact: [contact?.firstName, contact?.lastName].filter(Boolean).join(' '),
    owners: (lead?.owners ?? []).map(({ organizationMembershipId, user }) => ({
      organizationMembershipId,
      userId: user.id,
      avatarIndex: user.avatarIndex,
      firstName: user.firstName,
      lastName: user.lastName,
    })),
    deals: buildDeals(lead?.deals ?? []),
    contactCreated: formatDateTime(contact?.createdAt),
    contactUpdated: formatDateTime(contact?.updatedAt),
    created: formatDateTime(lead?.createdAt),
    updated: formatDateTime(lead?.updatedAt),
  };

  populateCommsRow(row, details.comms);

  for (const customField of [...(lead?.customFields ?? []), ...(contact?.customFields ?? [])]) {
    row.customFields[customField.customFieldId] = customField;
  }

  rowByDetails.set(details, row);
  return row;
};

const RecordsRoute = () => {
  const navigate = useNavigate({ from: '/records' });

  const screens = Grid.useBreakpoint();
  const isMobile = screens.md === false;
  const { account, selectedOrganization, selectedMembership } = useRouteContext({
    from: '/_authenticated/_app',
  });
  const organizationId = selectedOrganization.id;
  const { connections, emailConnection, canSendEmail, canSendSms } = useCommunicationCapabilities();
  const { can } = usePermissions();
  const canEditRecords = can('records', 'edit');

  const queryClient = useQueryClient();
  const bulkArchiveLeads = useBulkArchiveLeads();
  const bulkArchiveContacts = useBulkArchiveContacts();

  const insets = useLayoutInsets();
  const [createRecordOpen, setCreateRecordOpen] = useState(false);
  const { isSideNav } = useNavigationPosition();
  const { uiContext, panel } = useAiAssistant();

  const handleRecordsInvalidate = useCallback(() => {
    [getSearchRecordsUrl, getLoadRecordDetailsUrl, getCountRecordsUrl].forEach((getUrl) =>
      queryClient.invalidateQueries({ queryKey: [getUrl(organizationId)] }),
    );
  }, [queryClient, organizationId]);

  const [compose, setCompose] = useState<{
    channel: 'email' | 'sms';
    target: BulkRecipientsBodyDto;
  } | null>(null);
  const { mode, groupBy, search = '' } = Route.useSearch();
  const [debouncedSearch] = useDebouncedValue(search, 300);

  const setSearchParam = (patch: Record<string, string | undefined>) =>
    navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true, resetScroll: false });

  const [columnPickerVisibility, setColumnPickerVisibility] = useState<Record<string, boolean>>({});
  const [scrollToEndToken, setScrollToEndToken] = useState(0);

  const [createField, setCreateField] = useState<{
    name: string;
    type: ReturnType<typeof normalizeCustomFieldCreateType>;
  } | null>(null);

  const openTableCompose = useCallback(
    (channel: 'email' | 'sms', contactId: string) => {
      if (channel === 'email' && !emailConnection) {
        message.warning('Connect a mailbox first');
        return;
      }
      setCompose({
        channel,
        target: { entityType: 'contact', scope: 'ids', ids: [contactId] },
      });
    },
    [emailConnection],
  );

  const { data: customFieldsData, isPending: isCustomFieldsPending } =
    useListCustomFields(organizationId);
  const customFields = useMemo(
    () => customFieldsData?.data?.items?.filter((field) => field.recordType !== 'deal'),
    [customFieldsData?.data?.items],
  );
  const { data: smartViewFieldsData } = useListSmartViewAvailableFields(organizationId);

  const baseColumns = useMemo(
    () =>
      buildColumnsFromFields<RecordRow>(smartViewFieldsData?.data?.items ?? [], customFields ?? []),
    [customFields, smartViewFieldsData?.data?.items],
  );

  const allColumns = useMemo(() => tableColumnsToSmartViewColumns(baseColumns), [baseColumns]);

  const smartView = useSmartView({ organizationId, entityType: 'record', allColumns });

  const viewKey = `${smartView.activeViewId}|${smartView.resetKey}`;
  const prevViewKeyRef = useRef(viewKey);
  if (prevViewKeyRef.current !== viewKey) {
    prevViewKeyRef.current = viewKey;
    setColumnPickerVisibility({});
  }

  const selectFields = (customFields ?? []).filter(
    (field) => field.type === 'single_select' || field.type === 'multi_select',
  );
  const [viewMode, setViewMode] = useState<'grid' | 'kanban'>(mode ?? 'grid');
  const { density, toolbarTheme } = useDensity(viewMode);
  const [isSwitchingView, startViewSwitch] = useTransition();
  useEffect(() => {
    setViewMode(mode ?? 'grid');
  }, [mode]);

  const activeGroupByField = selectFields.find((field) => field.id === groupBy) ?? selectFields[0];
  const [groupByOpen, setGroupByOpen] = useState(false);

  const searchBody = useMemo<Omit<SearchRecordsBodyDto, 'cursor' | 'take'>>(
    () => ({
      ...(smartView.activeView?.filter && { filter: smartView.activeView.filter }),
      ...(smartView.sort && { sort: smartView.sort }),
      ...(debouncedSearch.trim() && { search: debouncedSearch.trim() }),
    }),
    [debouncedSearch, smartView.activeView?.filter, smartView.sort],
  );
  const countBody = {
    filter: searchBody.filter,
    search: searchBody.search,
    smartViewId: searchBody.search ? undefined : (smartView.activeViewId ?? undefined),
  };

  const {
    data: searchData,
    isPending,
    isError,
    isPlaceholderData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: [getSearchRecordsUrl(organizationId), searchBody] as const,
    queryFn: ({ pageParam, signal }) =>
      searchRecords(organizationId, { ...searchBody, cursor: pageParam, take: 50 }, { signal }),
    getNextPageParam: (lastPage) => lastPage.data.nextCursor ?? undefined,
    initialPageParam: undefined as string | undefined,
    placeholderData: (previous, previousQuery) =>
      isEqual(previousQuery?.queryKey[1].filter, searchBody.filter) ? previous : undefined,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });

  const { data: countData } = useQuery({
    queryKey: [getCountRecordsUrl(organizationId), countBody] as const,
    queryFn: ({ signal }) => countRecords(organizationId, countBody, { signal }),
    placeholderData: (previous, previousQuery) =>
      isEqual(previousQuery?.queryKey[1].filter, countBody.filter) ? previous : undefined,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });

  const visibleCustomFieldColumns = useMemo(
    () =>
      smartView.columnsConfig
        .filter(
          (column) =>
            column.id.includes('_custom_field.') &&
            (columnPickerVisibility[column.id] ?? column.visible !== false),
        )
        .map((column) => column.id)
        .sort(),
    [smartView.columnsConfig, columnPickerVisibility],
  );

  const pageIds = useMemo(
    () => (searchData?.pages ?? []).map((page) => page.data.items.map((item) => item.id)),
    [searchData],
  );

  const detailsResults = useQueries({
    queries: pageIds.map((ids) => ({
      queryKey: [getLoadRecordDetailsUrl(organizationId), ids, visibleCustomFieldColumns],
      queryFn: ({ signal }: { signal: AbortSignal }) =>
        loadRecordDetails(organizationId, { ids, columns: visibleCustomFieldColumns }, { signal }),
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      enabled: ids.length > 0,
    })),
  });
  const detailsKey = detailsResults.map((result) => result.dataUpdatedAt).join();

  const rows = useMemo(() => {
    const detailsById = new Map(
      [
        ...queryClient
          .getQueriesData<Awaited<ReturnType<typeof loadRecordDetails>>>({
            queryKey: [getLoadRecordDetailsUrl(organizationId)],
          })
          .map(([, cached]) => cached),
        ...detailsResults.map((result) => result.data),
      ].flatMap((response) =>
        (response?.data.items ?? []).map((details) => [details.id, details] as const),
      ),
    );

    return (searchData?.pages ?? []).flatMap((page) =>
      page.data.items.map((item) => {
        const details = detailsById.get(item.id);
        return details
          ? buildRecordRow(details)
          : {
              ...buildBlankRow(item.id),
              leadId: item.leadId,
              contactId: item.primaryContactId,
              __previewKind: 'loading' as const,
            };
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchData, detailsKey]);

  const isListEmpty = !isPending && !isError && !isPlaceholderData && rows.length === 0;

  const loadMoreRef = useRef(() => {});
  useEffect(() => {
    loadMoreRef.current = () => {
      if (hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    };
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);
  const handleLoadMore = useCallback(() => loadMoreRef.current(), []);

  const isDefaultView = smartView.activeView?.isDefault ?? true;
  const isEmptyPreview = isListEmpty && isDefaultView && !searchBody.search;
  const isSmartViewNoRows = isListEmpty && !isDefaultView;

  const tableRows = useMemo(
    () =>
      isEmptyPreview
        ? [
            ...RECORD_PREVIEW_RECORDS.map((record, recordIndex) => ({
              ...buildBlankRow(`__preview-mock-${recordIndex}`),
              __previewKind: 'mock' as const,
              leadWebsiteValue: record.website,
              name: record.name,
              contact: `${record.contact.firstName} ${record.contact.lastName}`,
              email: record.email,
              phone: record.phone,
              contactCreated: record.created,
              contactUpdated: record.updated,
              created: record.created,
              updated: record.updated,
              previewOwnerLabel: `${record.owner.firstName} ${record.owner.lastName}`,
              previewDealsLine: record.deals,
            })),
            ...Array.from({ length: 5 }, (_, skeletonIndex) => ({
              ...buildBlankRow(`__preview-skeleton-${skeletonIndex}`),
              __previewKind: 'skeleton' as const,
            })),
          ]
        : isPending
          ? Array.from({ length: 20 }, (_, pendingIndex) => ({
              ...buildBlankRow(`__pending-${pendingIndex}`),
              __previewKind: 'pending' as const,
            }))
          : rows,
    [isEmptyPreview, isPending, rows],
  );

  const cellCtx = useMemo(
    () => ({
      organizationId,
      can,
      canSendEmail,
      canSendSms,
      onInvalidate: handleRecordsInvalidate,
      onCompose: openTableCompose,
    }),
    [organizationId, can, canSendEmail, canSendSms, handleRecordsInvalidate, openTableCompose],
  );

  const columnsWithRenderers = useMemo(
    (): DataGridColumn<RecordRow>[] =>
      baseColumns.map((col) => {
        const renderText = (value: (row: RecordRow) => unknown, className?: string) => ({
          renderCell: ({ row, isEmptyRow }: { row: RecordRow; isEmptyRow?: boolean }) => {
            const text = value(row);
            return (
              <RecordGridCell
                kind="text"
                ctx={cellCtx}
                isEmptyRow={isEmptyRow}
                previewKind={row.__previewKind}
                previewValue={typeof text === 'string' ? text : undefined}
                value={typeof text === 'string' || typeof text === 'number' ? String(text) : ''}
                className={className ?? (typeof text === 'number' ? 'font-mono' : undefined)}
              />
            );
          },
        });

        switch (col.key) {
          case 'lead.name':
            return {
              ...col,
              name: 'Companies',
              frozen: false,
              enablePin: true,
              enableHide: false,
              renderHeaderCell: () => <TableHeaderLabel icon="leadName" label="Companies" />,
              renderCell: ({ row, isEmptyRow }) =>
                row.leadId || !row.contactId ? (
                  <RecordGridCell
                    kind="companyName"
                    ctx={cellCtx}
                    isEmptyRow={isEmptyRow}
                    previewKind={row.__previewKind}
                    previewValue={row.name}
                    leadId={row.leadId}
                    value={row.name}
                    className="lead-name"
                  />
                ) : (
                  <RecordGridCell
                    kind="addCompany"
                    ctx={cellCtx}
                    isEmptyRow={isEmptyRow}
                    previewKind={row.__previewKind}
                    contactId={row.contactId}
                  />
                ),
            };

          case 'contact.fullName':
            return {
              ...col,
              name: 'People',
              renderHeaderCell: () => <TableHeaderLabel icon="contact" label="People" />,
              renderCell: ({ row, isEmptyRow }) =>
                row.leadId && !row.contactId ? (
                  <RecordGridCell
                    kind="addPerson"
                    ctx={cellCtx}
                    isEmptyRow={isEmptyRow}
                    previewKind={row.__previewKind}
                    leadId={row.leadId}
                  />
                ) : (
                  <RecordGridCell
                    kind="personName"
                    ctx={cellCtx}
                    isEmptyRow={isEmptyRow}
                    previewKind={row.__previewKind}
                    previewValue={row.contact}
                    className="data-grid-cell--contact-created-updated"
                    contactId={row.contactId ?? ''}
                    firstName={row.contactFirstName}
                    lastName={row.contactLastName}
                    linkToLeadId={row.leadId ?? undefined}
                    otherPeopleCount={row.otherPeopleCount}
                  />
                ),
            };

          case 'contact.emails':
            return {
              ...col,
              renderCell: ({ row, isEmptyRow }) => (
                <RecordGridCell
                  kind="emails"
                  ctx={cellCtx}
                  isEmptyRow={isEmptyRow}
                  previewKind={row.__previewKind}
                  previewValue={row.email}
                  contactId={row.contactId}
                  values={row.contactEmails}
                />
              ),
            };

          case 'contact.phoneNumbers':
            return {
              ...col,
              renderCell: ({ row, isEmptyRow }) => (
                <RecordGridCell
                  kind="phones"
                  ctx={cellCtx}
                  isEmptyRow={isEmptyRow}
                  previewKind={row.__previewKind}
                  previewValue={row.phone}
                  contactId={row.contactId}
                  values={row.contactPhoneNumbers}
                />
              ),
            };

          case 'lead.websites':
            return {
              ...col,
              renderCell: ({ row, isEmptyRow }) => (
                <RecordGridCell
                  kind="website"
                  ctx={cellCtx}
                  isEmptyRow={isEmptyRow}
                  previewKind={row.__previewKind}
                  previewValue={row.leadWebsiteValue}
                  leadId={row.leadId}
                  websiteId={row.leadWebsiteId}
                  websiteValue={row.leadWebsiteValue}
                />
              ),
            };

          case 'lead.owners':
            return {
              ...col,
              renderCell: ({ row, isEmptyRow }) => (
                <RecordGridCell
                  kind="owners"
                  ctx={cellCtx}
                  isEmptyRow={isEmptyRow}
                  previewKind={row.__previewKind}
                  previewValue={row.previewOwnerLabel}
                  leadId={row.leadId}
                  owners={row.owners}
                />
              ),
            };

          case 'lead.deals':
            return {
              ...col,
              name: 'Pipeline stage',
              renderHeaderCell: () => <TableHeaderLabel icon="pipeline" label="Pipeline stage" />,
              renderCell: ({ row, isEmptyRow }) => (
                <RecordGridCell
                  kind="deals"
                  ctx={cellCtx}
                  isEmptyRow={isEmptyRow}
                  previewKind={row.__previewKind}
                  previewValue={row.previewDealsLine}
                  leadId={row.leadId}
                  deals={row.deals}
                />
              ),
            };

          case 'contact.createdAt':
          case 'lead.createdAt':
            return {
              ...col,
              renderHeaderCell: () => (
                <TableHeaderLabel icon="dateCreated" label="Date created" lock />
              ),
              ...renderText(
                (row) => (col.key === 'lead.createdAt' ? row.created : row.contactCreated),
                'data-grid-cell--contact-created-updated',
              ),
            };

          case 'contact.updatedAt':
          case 'lead.updatedAt':
            return {
              ...col,
              renderHeaderCell: () => (
                <TableHeaderLabel icon="dateUpdated" label="Date updated" lock />
              ),
              ...renderText(
                (row) => (col.key === 'lead.updatedAt' ? row.updated : row.contactUpdated),
                'data-grid-cell--contact-created-updated',
              ),
            };

          case 'comms.replyRate':
            return {
              ...col,
              renderCell: ({ row, isEmptyRow }) => (
                <RecordGridCell
                  kind="replyRate"
                  ctx={cellCtx}
                  isEmptyRow={isEmptyRow}
                  previewKind={row.__previewKind}
                  replyRate={
                    typeof row['comms.replyRate'] === 'number' ? row['comms.replyRate'] : null
                  }
                />
              ),
            };

          default: {
            if (String(col.key).startsWith('comms.')) {
              return { ...col, ...renderText((row) => row[col.key]) };
            }

            const customField = customFields?.find(
              (field) => `${field.recordType}_custom_field.${field.id}` === col.key,
            );
            if (!customField) {
              return col;
            }

            return {
              ...col,
              renderCell: ({ row, isEmptyRow }) =>
                customField.recordType === 'contact'
                  ? (row.contactId || row.__previewKind) && (
                      <RecordGridCell
                        kind="contactCustomField"
                        ctx={cellCtx}
                        isEmptyRow={isEmptyRow}
                        previewKind={row.__previewKind}
                        previewValue="—"
                        contactId={row.contactId ?? ''}
                        field={customField}
                        value={row.customFields[customField.id]}
                      />
                    )
                  : (row.leadId || row.__previewKind) && (
                      <RecordGridCell
                        kind="leadCustomField"
                        ctx={cellCtx}
                        isEmptyRow={isEmptyRow}
                        previewKind={row.__previewKind}
                        previewValue="—"
                        leadId={row.leadId ?? ''}
                        field={customField}
                        value={row.customFields[customField.id]}
                      />
                    ),
            };
          }
        }
      }),
    [baseColumns, cellCtx, customFields],
  );

  const columnVisibleById = useMemo(
    () => ({ ...columnPickerVisibility, 'lead.name': true }),
    [columnPickerVisibility],
  );
  const handleColumnVisibilityChange = useCallback(
    (visibleById: Record<string, boolean>) => {
      setColumnPickerVisibility((prev) => ({ ...prev, ...visibleById }));
      smartView.scheduleLayoutSave();
    },
    [smartView],
  );

  const getRowKey = useCallback((row: RecordRow) => row.id, []);
  const flashingRows = useAiFlashRows<RecordRow>({ rows: tableRows, getRowKey });

  const aiScreenColumns = useMemo(
    () =>
      smartView.columnsConfig
        .filter(
          (column) =>
            column.id === 'lead.name' ||
            (columnPickerVisibility[column.id] ?? column.visible !== false),
        )
        .map((column) => ({ id: column.id, label: column.label })),
    [smartView.columnsConfig, columnPickerVisibility],
  );

  const totalMatching = countData?.data.total ?? rows.length;

  useAiUiSnapshot({
    entity: 'record',
    visibleCount: rows.length,
    columns: aiScreenColumns,
    total: totalMatching,
    hasMore: hasNextPage ?? false,
    filter: smartView.activeView?.filter ?? undefined,
    search: searchBody.search,
    sort: smartView.sort,
    enabled: !isEmptyPreview,
  });

  const selection = useTableSelection(
    `${smartView.activeViewId ?? '__default__'}|${JSON.stringify(
      smartView.activeView?.filter ?? null,
    )}|${debouncedSearch}`,
  );
  const { selectedRows, setSelectedRows } = selection;
  const handleSelectCount = useCallback(
    async (count: number) => {
      const loadedIds = (data: typeof searchData) => (data?.pages ?? []).flatMap((page) => page.data.items.map((item) => item.id));
      let data = searchData;
      let hasMore = hasNextPage;
      while (loadedIds(data).length < count && hasMore) {
        ({ data, hasNextPage: hasMore } = await fetchNextPage());
      }

      setSelectedRows(new Set(loadedIds(data).slice(0, count)));
    },
    [searchData, hasNextPage, fetchNextPage, setSelectedRows],
  );
  const selectAll = useMemo(
    () => ({ total: totalMatching, ...selection.selectAll, onSelectCount: handleSelectCount }),
    [totalMatching, selection.selectAll, handleSelectCount],
  );
  const effectiveSelectedCount = selection.count(totalMatching);
  const isAllSelection = selection.mode === 'all';

  const selectedTargets = useMemo(
    () => rows.filter((row) => selectedRows.has(row.id)),
    [rows, selectedRows],
  );
  const recordSelection = useMemo(
    () =>
      isAllSelection
        ? {
            scope: 'all' as const,
            filter: searchBody.filter,
            search: searchBody.search,
            excludedIds: selection.excludedIds,
          }
        : { scope: 'ids' as const, ids: [...selectedRows] },
    [isAllSelection, searchBody, selectedRows, selection.excludedIds],
  );

  useEffect(() => {
    if (!selection.hasSelection(totalMatching)) {
      uiContext.clearSelection();
      return;
    }

    uiContext.clearScopedFlow();
    uiContext.setSelection({
      entity: 'record',
      ids: rows.flatMap((row) => (selection.isSelected(row.id) ? [row.id] : [])),
      scope: isAllSelection ? 'all' : 'ids',
      total: totalMatching,
      excludedIds: isAllSelection ? selection.excludedIds : undefined,
    });
  }, [selection, rows, uiContext, totalMatching, isAllSelection]);

  useEffect(() => {
    panel.sync();
  }, [effectiveSelectedCount, selection.mode, panel]);

  useEffect(() => {
    return () => {
      uiContext.clearSelection();
      uiContext.setSelectionDelete(null);
    };
  }, [uiContext]);

  const assignOwnerAction = useAssignOwnerAction({
    organizationId,
    selection: recordSelection,
    skippedCount: selectedTargets.filter((row) => !row.leadId).length,
    ownersPerRecord: isAllSelection
      ? null
      : selectedTargets
          .filter((row) => row.leadId)
          .map((row) => row.owners.map((owner) => owner.organizationMembershipId)),
    onDone: selection.clear,
  });
  const runAgentsAction = useRunAgentsAction({
    organizationId,
    entityType: 'record',
    selection: recordSelection,
    onDone: selection.clear,
  });
  const mergeAction = useMergeRecordsAction({
    organizationId,
    rows: selectedTargets,
    onDone: selection.clear,
  });
  const callAction = useCallRecordAction({
    membershipId: selectedMembership.id,
    selection: isAllSelection ? {} : { ids: [...selectedRows] },
    rows: rows.map((row) => ({
      recordId: row.id,
      companyId: row.leadId,
      isSelected: selectedRows.has(row.id),
      contactId: row.contactId,
      contactName: row.contact,
      companyName: row.name,
      phoneNumbers: row.contactPhoneNumbers,
      owners: row.owners,
    })),
    sourceLabel: smartView.activeView?.name,
    onDone: selection.clear,
  });
  const bulkTarget = useMemo(
    () => ({ entityType: 'record' as const, ...recordSelection }),
    [recordSelection],
  );

  const handleBulkArchive = useCallback(() => {
    if (effectiveSelectedCount === 0) {
      return;
    }

    Modal.confirm({
      title: isAllSelection
        ? `Archive all ${effectiveSelectedCount} record(s) matching this view?`
        : `Archive ${effectiveSelectedCount} record(s)?`,
      content:
        'The selected companies, their deals and the people linked only to them will be archived',
      okText: 'Archive',
      okButtonProps: { danger: true, 'data-testid': 'confirm' },
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await Promise.all([
            bulkArchiveLeads.mutateAsync({ organizationId, data: recordSelection }),
            bulkArchiveContacts.mutateAsync({ organizationId, data: recordSelection }),
          ]);
          selection.clear();
          message.success('Archived');
        } catch {
          message.error('Failed to archive');
        }

        handleRecordsInvalidate();
      },
    });
  }, [
    bulkArchiveContacts,
    bulkArchiveLeads,
    effectiveSelectedCount,
    handleRecordsInvalidate,
    isAllSelection,
    organizationId,
    recordSelection,
    selection,
  ]);

  useEffect(() => {
    uiContext.setSelectionDelete(
      canEditRecords && effectiveSelectedCount > 0 ? handleBulkArchive : null,
    );
  }, [uiContext, canEditRecords, effectiveSelectedCount, handleBulkArchive]);

  const toPickerItem = (column: SmartViewColumnConfig) => {
    const isCompany = column.id === 'lead.name';
    const customField = customFields?.find(
      (field) => `${field.recordType}_custom_field.${field.id}` === column.id,
    );
    const field = smartViewFieldsData?.data?.items?.find(({ key }) => key === column.id);

    return {
      id: column.id,
      label: column.label,
      fieldType: normalizeColumnPickerFieldType(customField?.type ?? field?.columnType),
      checked: isCompany || (columnPickerVisibility[column.id] ?? column.visible !== false),
      locked: isCompany,
    };
  };
  const columnsForPicker = allColumns.map(
    (column) => smartView.columnsConfig.find((config) => config.id === column.id) ?? column,
  );

  const viewSegmented = (
    <Segmented<'grid' | 'kanban'>
      data-testid="records-mode"
      shape="round"
      styles={{ label: { minHeight: 'calc(var(--ant-control-height) - 6px)' } }}
      value={viewMode}
      onChange={(next) => {
        setGroupByOpen(false);
        startViewSwitch(() => {
          setViewMode(next);
          setSearchParam({ mode: next === 'kanban' ? 'kanban' : undefined });
        });
      }}
      options={[
        {
          value: 'grid',
          title: 'Grid',
          label: (
            <Flex align="center" justify="center" style={{ height: 20 }}>
              <Table size={13} />
            </Flex>
          ),
        },
        {
          value: 'kanban',
          title: activeGroupByField ? `Grouped by ${activeGroupByField.name}` : 'Kanban',
          label: (
            <Flex
              align="center"
              justify="center"
              gap={6}
              style={{ height: 20 }}
              onClick={() => {
                if (viewMode === 'kanban' && activeGroupByField) {
                  setGroupByOpen((isOpen) => !isOpen);
                }
              }}
            >
              <KanbanIcon size={13} style={{ flexShrink: 0 }} />
              {viewMode === 'kanban' && !isMobile && (
                <Flex align="center" gap={4} style={{ height: 20 }}>
                  <TextEllipsis style={{ maxWidth: 120, fontSize: 13, lineHeight: '20px' }}>
                    {activeGroupByField?.name ?? 'Kanban'}
                  </TextEllipsis>
                  {activeGroupByField && <ChevronDown size={12} style={{ flexShrink: 0 }} />}
                </Flex>
              )}
            </Flex>
          ),
        },
      ]}
    />
  );

  return (
    <Flex vertical style={{ height: `calc(100dvh - ${insets.top}px)` }}>
      <Flex
        vertical={isMobile}
        align={isMobile ? 'stretch' : isSideNav ? 'center' : 'flex-end'}
        justify="space-between"
        gap={isMobile ? 0 : 8}
        style={{
          padding: isMobile
            ? `8px 12px ${isSideNav ? 12 : 0}px`
            : isSideNav
              ? '16px 24px 8px 0'
              : '0 24px',
          minHeight: isMobile || isSideNav ? undefined : density === 'compact' ? 40 : 44,
          backgroundColor: 'var(--ant-color-bg-layout)',
          flexShrink: 0,
        }}
      >
        <Flex
          align="flex-end"
          gap={8}
          flex={1}
          style={{
            width: isMobile ? '100%' : undefined,
            overflowX: isMobile ? 'auto' : undefined,
            scrollbarWidth: 'none',
            order: isMobile ? 2 : undefined,
          }}
          className="records-toolbar-tabs"
        >
          {isSideNav && (
            <GlobalSearch
              organizationId={organizationId}
              tableSearch={{
                value: search,
                onChange: (value) => setSearchParam({ search: value || undefined }),
                placeholder: 'Search records',
              }}
            />
          )}
          <ConfigProvider theme={toolbarTheme}>
            <SmartViewTabs
              smartView={smartView}
              organizationId={organizationId}
              entityType="record"
              hasData={!isEmptyPreview}
              disabled={isEmptyPreview}
              onEnterAiFlow={selection.clear}
            />
          </ConfigProvider>
        </Flex>
        <Flex
          align="center"
          gap={8}
          justify={isMobile ? 'space-between' : 'flex-end'}
          style={{
            width: isMobile ? '100%' : undefined,
            marginBottom: isSideNav ? 0 : 6,
            order: isMobile ? 1 : undefined,
          }}
        >
          {isSideNav && <HeaderActions organizationId={organizationId} variant="side" />}
          {selectFields.length > 0 ? (
            <GroupByButton
              groups={(['lead', 'contact'] as const).map((recordType) => ({
                key: recordType,
                label: `${customFieldRecordTypeLabels[recordType]} fields`,
                options: selectFields
                  .filter((field) => field.recordType === recordType)
                  .map((field) => ({
                    id: field.id,
                    label: field.name,
                    icon:
                      field.type === 'multi_select' ? (
                        <ListChecks size={14} />
                      ) : (
                        <CircleDot size={14} />
                      ),
                  })),
              }))}
              value={activeGroupByField?.id ?? null}
              onChange={(fieldId) => {
                setSearchParam({ groupBy: fieldId });
                setGroupByOpen(false);
              }}
              open={groupByOpen}
              onOpenChange={setGroupByOpen}
              trigger={[]}
              placement="bottomLeft"
            >
              {viewSegmented}
            </GroupByButton>
          ) : (
            viewSegmented
          )}
          {viewMode === 'kanban' && (
            <SortByButton
              testId="records-sort"
              options={[
                { id: 'lead.name', label: 'Company name' },
                { id: 'contact.fullName', label: 'Person name' },
                { id: 'lead.createdAt', label: 'Created' },
                { id: 'lead.updatedAt', label: 'Updated' },
              ]}
              value={smartView.sort ?? { field: 'lead.createdAt', direction: 'desc' }}
              onChange={(next) =>
                smartView.handleSortSnapshot(
                  next,
                  (next ? `${next.field}:${next.direction}` : '') !==
                    (smartView.activeView?.sort
                      ? `${smartView.activeView.sort.field}:${smartView.activeView.sort.direction}`
                      : ''),
                )
              }
            />
          )}
          <ColumnPickerButton
            testId="columns"
            groups={[
              {
                key: 'lead',
                label: 'Company fields',
                items: columnsForPicker
                  .filter(
                    (column) =>
                      !column.id.startsWith('contact') &&
                      !column.id.startsWith('comms.') &&
                      !column.id.includes('_custom_field.'),
                  )
                  .map(toPickerItem),
              },
              {
                key: 'contact',
                label: 'Person fields',
                items: columnsForPicker
                  .filter((column) => column.id.startsWith('contact.'))
                  .map(toPickerItem),
              },
              ...buildCommsPickerGroups(columnsForPicker, toPickerItem),
              {
                key: 'custom',
                label: 'Custom fields',
                items: columnsForPicker
                  .filter((column) => column.id.includes('_custom_field.'))
                  .map(toPickerItem),
              },
            ]}
            onItemCheckedChange={(columnId, visible) => {
              if (columnId === 'lead.name') {
                return;
              }
              setScrollToEndToken((token) => token + 1);
              setColumnPickerVisibility((prev) => ({ ...prev, [columnId]: visible }));
              smartView.scheduleLayoutSave();
            }}
            onCreateField={(name, type) =>
              setCreateField({ name, type: normalizeCustomFieldCreateType(type) })
            }
          />
        </Flex>
      </Flex>
      <TableSurface
        testId="records-grid"
        className={`table-surface--page-inset records-table${isSideNav ? '' : ' table-surface--page-inset-top-nav'}`}
      >
        {viewMode === 'kanban' ? (
          <div
            data-testid="records-kanban"
            style={{
              flex: 1,
              overflow: 'hidden',
              opacity: isSwitchingView ? 0.6 : 1,
              transition: 'opacity 0.15s',
            }}
          >
            {!isEmptyPreview && activeGroupByField ? (
              <CustomFieldKanban
                organizationId={organizationId}
                field={activeGroupByField}
                customFields={customFields ?? []}
                search={searchBody.search}
                filter={searchBody.filter}
                sort={searchBody.sort}
                isSideNav={isSideNav}
              />
            ) : (
              <RecordKanbanEmptyPreview
                organizationId={organizationId}
                reason={
                  isPending || isCustomFieldsPending ? null : isEmptyPreview ? 'records' : 'field'
                }
                isSideNav={isSideNav}
              />
            )}
          </div>
        ) : (
          <Flex vertical flex={1} style={{ position: 'relative', overflow: 'hidden' }}>
            <Flex
              vertical
              flex={1}
              className={isEmptyPreview ? 'table-surface__empty-preview-dim' : undefined}
              style={{ overflow: 'hidden' }}
            >
              <SmartViewTable<RecordRow>
                columns={columnsWithRenderers}
                rows={tableRows}
                rowKey="id"
                emptyRowsCount={1}
                flashingRows={mergeAction.flashingRows.size > 0 ? mergeAction.flashingRows : flashingRows}
                selectedRows={selectedRows}
                onSelectedRowsChange={setSelectedRows}
                selectAll={selectAll}
                scrollToEndToken={scrollToEndToken}
                smartView={smartView}
                columnVisibleById={columnVisibleById}
                onColumnVisibilityChange={handleColumnVisibilityChange}
                isError={isError}
                onLoadMore={handleLoadMore}
                isEmptyPreview={isEmptyPreview}
                emptyText="No records found"
                renderEmpty={
                  isSmartViewNoRows
                    ? () => (
                        <SmartViewNoResultsEmpty
                          label="records"
                          onClearFilters={() => smartView.switchView(null)}
                        />
                      )
                    : undefined
                }
              />
            </Flex>
            {isEmptyPreview && (
              <CrmEmptyStateLayout
                title="Let's get your CRM ready"
                body="Upload your companies and connect your email—then sit back. The system matches people with past conversations and quickly learns how you sell, speak, and operate… so it can engage new companies like you would."
                overlay
              >
                <Flex justify="center" gap={8}>
                  <Button
                    type="primary"
                    data-testid="records-empty-import"
                    onClick={() => navigate({ to: '/import/new' })}
                  >
                    Import
                  </Button>
                  <Button
                    data-testid="records-empty-create"
                    onClick={() => setCreateRecordOpen(true)}
                  >
                    Create record
                  </Button>
                </Flex>
              </CrmEmptyStateLayout>
            )}
          </Flex>
        )}
      </TableSurface>

      {createRecordOpen && (
        <CreateRecordDrawer
          organizationId={organizationId}
          onClose={() => setCreateRecordOpen(false)}
        />
      )}

      {createField && (
        <CreateCustomFieldDrawer
          organizationId={organizationId}
          open
          onClose={() => setCreateField(null)}
          initialName={createField.name}
          initialType={createField.type}
          onCreated={(field) => {
            queryClient.invalidateQueries({ queryKey: getListSmartViewAvailableFieldsQueryKey(organizationId) });

            if (field.recordType !== 'deal') {
              setScrollToEndToken((token) => token + 1);
              smartView.addColumn(`${field.recordType}_custom_field.${field.id}`);
            }
          }}
        />
      )}

      {compose && (
        <FloatingWindow storageKey="k3x_compose_window">
          {(dragHandle) =>
            compose.channel === 'email' ? (
              <EmailForm
                variant="card"
                organizationId={organizationId}
                connectionId={emailConnection?.id ?? ''}
                emailConnections={connections}
                senderName={[account.firstName, account.lastName].filter(Boolean).join(' ')}
                bulk={compose.target}
                dragHandle={dragHandle}
                onSuccess={() => {
                  setCompose(null);
                  selection.clear();
                }}
                onCancel={() => setCompose(null)}
              />
            ) : (
              <SmsForm
                variant="card"
                organizationId={organizationId}
                organizationMembershipId={selectedMembership.id}
                bulk={compose.target}
                dragHandle={dragHandle}
                onSuccess={() => {
                  setCompose(null);
                  selection.clear();
                }}
                onCancel={() => setCompose(null)}
              />
            )
          }
        </FloatingWindow>
      )}

      <SelectionActionsBar
        entity="record"
        count={effectiveSelectedCount}
        onAction={panel.newChat}
        onClear={selection.clear}
        actions={{
          assign: assignOwnerAction,
          merge: mergeAction,
          call: callAction,
          agents: runAgentsAction,
          email: {
            hidden: !can('emails', 'send'),
            disabled: !canSendEmail,
            tooltip: canSendEmail ? undefined : 'Connect a mailbox',
            onClick: () => setCompose({ channel: 'email', target: bulkTarget }),
          },
          sms: {
            hidden: !can('sms', 'send'),
            disabled: !canSendSms,
            tooltip: canSendSms ? undefined : 'Set up a phone number',
            onClick: () => setCompose({ channel: 'sms', target: bulkTarget }),
          },
          archive: { hidden: !canEditRecords, onClick: handleBulkArchive },
        }}
      />
    </Flex>
  );
};

export const Route = createFileRoute('/_authenticated/_app/records')({
  component: RecordsRoute,
  validateSearch: (params) =>
    z
      .object({
        mode: z.enum(['grid', 'kanban']).optional(),
        groupBy: z.string().optional(),
        search: z.string().optional(),
        smartView: z.string().optional(),
      })
      .parse(params),
});
